/**
 * Semi wide-load quote — lowboy / RGN / step-deck, not a 1-ton hotshot card.
 * Same billed-mile physics as Hotshot (round-trip × road factor) so pickup →
 * drop money matches the truck. Adders cover trailer, oversize, escort, permits.
 */

import {
  clampSemiFeet,
  isSemiPermitBy,
  isSemiTrailerId,
  semiIsOversize,
  semiNeedsEscort,
  type SemiPermitBy,
  type SemiTrailerId,
} from './semiWideLoadCatalog';
import { PARTNER_OPERATING_CITIES } from './partnerServiceArea';

const ROAD_FACTOR = 1.25;
const LOCAL_RADIUS_MILES = 20;

function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number | null {
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return null;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 10) / 10;
}

function billedMiles(oneWay: number): number {
  return Math.round(oneWay * 2 * ROAD_FACTOR * 10) / 10;
}

function isLocalRun(pickupLat: number, pickupLng: number, dropLat: number, dropLng: number): boolean {
  const near = (lat: number, lng: number) => PARTNER_OPERATING_CITIES.some((c) => {
    const mi = haversineMiles(lat, lng, c.lat, c.lng);
    return mi != null && mi <= LOCAL_RADIUS_MILES;
  });
  return near(pickupLat, pickupLng) && near(dropLat, dropLng);
}

export const SEMI_WIDE_PRICING = {
  minTripUsd: 1_250,
  localMinTripUsd: 650,
  /** Same calendar idiom as hotshot: today = Critical, tomorrow = Rush, 2+ days = Standard. */
  ratePerMileUsd: {
    standard: 6.25,
    rush: 7.75,
    critical: 9.75,
  },
  maxQuoteUsd: 25_000,
  trailerUsd: {
    lowboy: 250,
    rgn: 400,
    step_deck: 175,
  } as Record<SemiTrailerId, number>,
  oversizeUsd: 350,
  escortUsd: 750,
  permitPullUsd: 275,
} as const;

export type SemiUrgencyId = keyof typeof SEMI_WIDE_PRICING.ratePerMileUsd;

/** Whole days from today's noon to the ready day's noon (DST-safe, same as hotshot). */
export function semiLeadDays(readyDateIso: string | null | undefined, now: Date): number {
  if (!readyDateIso) return 99;
  const [y, m, d] = String(readyDateIso).split('-').map((n) => Number(n));
  if (!y || !m || !d) return 99;
  const readyNoon = new Date(y, m - 1, d, 12, 0, 0, 0).getTime();
  const todayNoon = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0).getTime();
  return Math.round((readyNoon - todayNoon) / 86_400_000);
}

export function semiUrgencyFromReadyDate(
  readyDateIso: string | null | undefined,
  now: Date,
): SemiUrgencyId {
  const lead = semiLeadDays(readyDateIso, now);
  if (lead <= 0) return 'critical';
  if (lead === 1) return 'rush';
  return 'standard';
}

/** Compact per-day calendar badge — only today (Critical) and tomorrow (Rush). */
export function semiReadyDayBadge(readyDateIso: string | null | undefined, now: Date): string {
  const lead = semiLeadDays(readyDateIso, now);
  if (lead <= 0) return `CRIT $${SEMI_WIDE_PRICING.ratePerMileUsd.critical.toFixed(2)}`;
  if (lead === 1) return `RUSH $${SEMI_WIDE_PRICING.ratePerMileUsd.rush.toFixed(2)}`;
  return '';
}

export type SemiWideQuoteInput = {
  pickupLat?: number | null;
  pickupLng?: number | null;
  dropLat?: number | null;
  dropLng?: number | null;
  trailerKind?: unknown;
  widthFt?: unknown;
  heightFt?: unknown;
  permitBy?: unknown;
  readyDateIso?: string | null;
  now?: Date;
};

export type SemiWideQuoteLine = {
  label: string;
  amountUsd: number;
};

export type SemiWideQuote = {
  trailerKind: SemiTrailerId;
  widthFt: number;
  heightFt: number;
  permitBy: SemiPermitBy;
  billedMiles: number | null;
  localRun: boolean;
  oversize: boolean;
  escort: boolean;
  urgency: SemiUrgencyId;
  ratePerMileUsd: number;
  totalCents: number;
  quoteReady: boolean;
  blockedReason: string | null;
  lines: SemiWideQuoteLine[];
};

export function formatSemiUsd(cents: number): string {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '$0';
  const usd = n / 100;
  return usd % 1 === 0 ? `$${usd.toFixed(0)}` : `$${usd.toFixed(2)}`;
}

export function estimateSemiWideQuote(input: SemiWideQuoteInput): SemiWideQuote {
  const trailerKind: SemiTrailerId = isSemiTrailerId(input.trailerKind) ? input.trailerKind : 'lowboy';
  const widthFt = clampSemiFeet(input.widthFt, 8.5, 20);
  const heightFt = clampSemiFeet(input.heightFt, 10, 18);
  const permitBy: SemiPermitBy = isSemiPermitBy(input.permitBy) ? input.permitBy : 'hauler';
  const oversize = semiIsOversize(widthFt, heightFt);
  const escort = semiNeedsEscort(widthFt, heightFt);
  const pickupLat = Number(input.pickupLat);
  const pickupLng = Number(input.pickupLng);
  const dropLat = Number(input.dropLat);
  const dropLng = Number(input.dropLng);
  const pinsOk = [pickupLat, pickupLng, dropLat, dropLng].every(Number.isFinite);
  const localRun = pinsOk
    ? isLocalRun(pickupLat, pickupLng, dropLat, dropLng)
    : false;
  const oneWay = pinsOk ? haversineMiles(pickupLat, pickupLng, dropLat, dropLng) : null;
  const billed = oneWay != null ? billedMiles(oneWay) : null;
  const samePoint = billed != null && billed < 1;
  const quoteReady = pinsOk && billed != null && !samePoint;

  const now = input.now || new Date();
  const urgency = semiUrgencyFromReadyDate(input.readyDateIso, now);
  const ratePerMileUsd = SEMI_WIDE_PRICING.ratePerMileUsd[urgency];
  const floorUsd = localRun ? SEMI_WIDE_PRICING.localMinTripUsd : SEMI_WIDE_PRICING.minTripUsd;
  const mileageUsd = billed != null
    ? Math.round(billed * ratePerMileUsd * 100) / 100
    : 0;
  const trailerUsd = SEMI_WIDE_PRICING.trailerUsd[trailerKind];
  const oversizeUsd = oversize ? SEMI_WIDE_PRICING.oversizeUsd : 0;
  const escortUsd = escort ? SEMI_WIDE_PRICING.escortUsd : 0;
  const permitUsd = permitBy === 'hauler' && oversize ? SEMI_WIDE_PRICING.permitPullUsd : 0;
  const rawTotal = Math.max(floorUsd, mileageUsd) + trailerUsd + oversizeUsd + escortUsd + permitUsd;
  const totalUsd = Math.min(SEMI_WIDE_PRICING.maxQuoteUsd, Math.round(rawTotal * 100) / 100);

  const lines: SemiWideQuoteLine[] = [];
  if (billed != null) {
    const tier = urgency === 'critical' ? 'day-of' : urgency === 'rush' ? 'next-day' : null;
    lines.push({
      label: localRun
        ? `In-town · ${billed.toFixed(1)} mi billed · $${ratePerMileUsd.toFixed(2)}/mi${tier ? ` · ${tier}` : ''}`
        : `${billed.toFixed(1)} mi billed (round-trip) · $${ratePerMileUsd.toFixed(2)}/mi${tier ? ` · ${tier}` : ''}`,
      amountUsd: Math.max(floorUsd, mileageUsd),
    });
  }
  lines.push({
    label: trailerKind === 'rgn' ? 'RGN' : trailerKind === 'step_deck' ? 'Step-deck' : 'Lowboy',
    amountUsd: trailerUsd,
  });
  if (oversizeUsd > 0) lines.push({ label: 'Oversize', amountUsd: oversizeUsd });
  if (escortUsd > 0) lines.push({ label: 'Pilot / escort', amountUsd: escortUsd });
  if (permitUsd > 0) lines.push({ label: 'Hauler pulls permits', amountUsd: permitUsd });

  return {
    trailerKind,
    widthFt,
    heightFt,
    permitBy,
    billedMiles: billed,
    localRun,
    oversize,
    escort,
    urgency,
    ratePerMileUsd,
    totalCents: Math.round(totalUsd * 100),
    quoteReady,
    blockedReason: !pinsOk
      ? 'Pin pickup and drop.'
      : samePoint
        ? 'Pickup and drop look like the same pin.'
        : null,
    lines,
  };
}
